import { appendCats, loadMore, resetGallery, shuffleGrid, bindScroll } from './gallery';
import { createLightbox } from './lightbox';
import { catUrl, filterFromUrl, updateCrumbs, bindFilters } from './filters';
import { requireEl } from './dom';
import { trackMetrikaHit } from './metrika';
import { initVisitCounters } from './visitors';
import { updateSeo, updatePermalinkSeo } from './seo';
import { fetchCatByPublicId } from './api';
import { mapSingle } from './batch';
import { hideComments } from './comments';
import { parsePermalinkId, parsePhotoFromQuery } from './share';
import { initVisitorIdentity } from './visitorId';
import { state, els, galleryEpoch } from './state';

declare global {
  interface Window {
    setFilter: (name: string) => void;
  }
}

const lightbox = createLightbox();
let permalinkMode = false;

function applyFilter(name: string, pushHistory: boolean): void {
  if (permalinkMode) {
    hideComments();
    permalinkMode = false;
  }
  state.filter = name;
  document.querySelectorAll<HTMLButtonElement>('.bar button[data-filter]').forEach(b => {
    b.classList.toggle('on', b.dataset.filter === name);
  });
  updateCrumbs(name, n => applyFilter(n, true));
  updateSeo(name, catUrl(name));
  updateShuffleButton();
  if (pushHistory) {
    history.pushState({ cat: name }, '', catUrl(name));
    trackMetrikaHit(catUrl(name));
  }
  void resetGallery(lightbox.open).then(() => openPhotoFromQuery());
}

window.setFilter = name => applyFilter(name, true);

async function onScrollLoad(): Promise<void> {
  const gen = galleryEpoch;
  const filter = state.filter;
  const batch = await loadMore(gen);
  if (gen !== galleryEpoch || state.filter !== filter) return;
  if (batch?.length) appendCats(batch, lightbox.open, gen);
}

function updateShuffleButton(): void {
  const btn = requireEl<HTMLButtonElement>('shuffle');
  btn.disabled = state.filter === 'liked';
  btn.title = state.filter === 'liked' ? 'недоступно в «♥ мои»' : '';
}

function openPhotoFromQuery(): void {
  const photo = parsePhotoFromQuery();
  if (!photo) return;
  const idx = state.allCats.findIndex(c => c.url === photo);
  if (idx >= 0) lightbox.open(idx);
}

async function openPermalink(publicId: string): Promise<void> {
  permalinkMode = true;
  hideComments();
  state.filter = 'all';
  document.querySelectorAll<HTMLButtonElement>('.bar button[data-filter]').forEach(b => {
    b.classList.toggle('on', b.dataset.filter === 'all');
  });
  updateCrumbs('all', n => applyFilter(n, true));

  const cat = await fetchCatByPublicId(publicId);
  if (!cat) {
    document.title = 'не найдено — imagecat';
    elsLoaderError();
    return;
  }

  updatePermalinkSeo(publicId, cat.width ?? 480, cat.height ?? 360, cat.url);
  state.allCats = [];
  state.nextId = 1;
  state.offset = 0;
  state.ended = true;
  els.grid.innerHTML = '';
  const item = mapSingle(cat, state);
  appendCats([item], lightbox.open, galleryEpoch);
  lightbox.open(0);
  history.replaceState({ permalink: publicId }, '', `/i/${publicId}`);
  trackMetrikaHit(`/i/${publicId}`);
}

function elsLoaderError(): void {
  const loader = requireEl<HTMLElement>('loader');
  loader.style.display = 'block';
  loader.textContent = 'котик не найден';
}

function startGallery(): void {
  requireEl<HTMLElement>('updated').textContent =
    new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });

  bindFilters(applyFilter);
  updateShuffleButton();
  requireEl<HTMLButtonElement>('shuffle').onclick = () => shuffleGrid(lightbox.open);

  window.addEventListener('popstate', () => {
    const id = parsePermalinkId(location.pathname);
    if (id) {
      void openPermalink(id);
      return;
    }
    const name = filterFromUrl();
    applyFilter(name, false);
    trackMetrikaHit(catUrl(name));
  });

  const permalinkId = parsePermalinkId(location.pathname);
  if (permalinkId) {
    void openPermalink(permalinkId);
  } else {
    const initial = filterFromUrl();
    const canonical = catUrl(initial);
    const canonicalPath = new URL(canonical, location.origin).pathname;
    if (location.pathname !== canonicalPath || (location.search !== '' && !parsePhotoFromQuery())) {
      history.replaceState({ cat: initial }, '', canonical);
      trackMetrikaHit(canonical);
    }
    applyFilter(initial, false);
  }

  bindScroll(onScrollLoad);
  initVisitCounters();
}

void initVisitorIdentity().then(startGallery);
